import React, { useState } from 'react';
import { RouteProp, useNavigation, useRoute } from '../../navigation/native';
import { KeyboardAvoidingView, Platform } from '../../web/react-native';
import { useDispatch } from 'react-redux';
import styled from '../../components/styled';

import { AppDispatch } from '../../store/store';
import { NavBar, NavTextButton, LoadingOverlay } from '../../components';
import { selectEditedSnippets, selectIsLogDetailLoading, shiftLogActions } from '../../store';
import { Route } from '../../app/Route';
import { CommentActionField, CompleteActionField, StartActionField } from './@actionFields';
import { RootStackParamList } from '../../app/Navigation';
import { TaskActionResponse } from '../../services/dataModels';
import { ScrollView } from '../../web/react-native';

const ActionScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation();
  const { params } = useRoute<RouteProp<RootStackParamList, 'EditingRowAction'>>();

  const isLoading = selectIsLogDetailLoading.useHook();
  const snippet = selectEditedSnippets.useHook();

  const rowData = snippet.data[params.rollupIndex ?? 0].rowData[params.rowIndex];
  const displayTemplateId = snippet.displayTemplateId;
  const { actualStartTime: currentStartTime = '', actualEndTime: currentEndTime = '' } = rowData;

  const [startTime, setStartTime] = useState(
    currentStartTime === ''
      ? (currentStartTime as string)
      : new Date((currentStartTime as string).replace(' ', 'T')).toISOString(),
  );

  const [endTime, setEndTime] = useState(
    currentEndTime === ''
      ? (currentEndTime as string)
      : new Date((currentEndTime as string).replace(' ', 'T')).toISOString(),
  );
  const [comment, setComment] = useState('');

  const handleCancelPressed = () => {
    navigation.goBack();
  };

  let action: {
    title: string;
    field: () => React.ReactNode;
    onConfirm: () => Promise<TaskActionResponse>;
    validate: () => boolean;
  };

  switch (params.action) {
    case 'Start':
      action = {
        title: 'Start',
        field: () => <StartActionField startTime={startTime} onChange={setStartTime} />,
        onConfirm: () =>
          dispatch(
            shiftLogActions.startTask({
              displayTemplateId: displayTemplateId,
              taskId: rowData.taskId as string,
              startTime: startTime,
            }),
          ).unwrap(),
        validate: () => startTime !== '' && startTime !== null,
      };
      break;

    case 'Complete':
      action = {
        title: 'Complete',
        field: () => (
          <CompleteActionField
            startTime={startTime}
            endTime={endTime}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
          />
        ),
        onConfirm: () =>
          dispatch(
            shiftLogActions.completeTask({
              displayTemplateId: displayTemplateId,
              taskId: rowData.taskId as string,
              startTime: startTime,
              endTime: endTime,
            }),
          ).unwrap(),
        validate: () =>
          startTime !== '' && startTime !== null && endTime !== '' && endTime !== null,
      };
      break;
    case 'Pause':
      action = {
        title: 'Pause',
        field: () => <CommentActionField onChange={setComment} />,
        onConfirm: () =>
          dispatch(
            shiftLogActions.pauseTask({
              displayTemplateId: displayTemplateId,
              taskId: rowData.taskId as string,
              comment: comment,
            }),
          ).unwrap(),

        validate: () => true,
      };
      break;
    case 'Resume':
      action = {
        title: 'Resume',
        field: () => {
          return <CommentActionField onChange={setComment} />;
        },
        onConfirm: () =>
          dispatch(
            shiftLogActions.resumeTask({
              displayTemplateId: displayTemplateId,
              taskId: rowData.taskId as string,
              comment: comment,
            }),
          ).unwrap(),
        validate: () => true,
      };
      break;
    case 'Cancel':
      action = {
        title: 'Cancel',
        field: () => {
          return <CommentActionField onChange={setComment} />;
        },
        onConfirm: () =>
          dispatch(
            shiftLogActions.cancelTask({
              displayTemplateId: displayTemplateId,
              taskId: rowData.taskId as string,
              comment: comment,
            }),
          ).unwrap(),
        validate: () => true,
      };
      break;
    default:
      return;
  }

  return (
    <>
      <NavBar
        title={action.title}
        leftButton={<NavTextButton text="Cancel" onPress={handleCancelPressed} />}
        rightButton={
          <NavTextButton
            text="Confirm"
            onPress={() => {
              action
                .onConfirm()
                .then(() => {
                  navigation.navigate(Route.editingRowScreen);
                })
                .catch(() => { });
            }}
            disabled={!action.validate()}
          />
        }
      />
      <LoadingOverlay visible={isLoading} />
      <SafeAreaView>
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          enabled
          keyboardVerticalOffset={100}>
          <ScrollView>
            {action.field()}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </>
  );
};

export default ActionScreen;

const SafeAreaView = styled.SafeAreaView`
  flex: 1;
  margin: 10px;
  min-height: 10px;
`;
